import { StartClient } from "@tanstack/react-start/client";
import { StrictMode, startTransition } from "react";
import { hydrateRoot } from "react-dom/client";
import { init as initAnalytics } from "@/lib/analytics";
import { applyStoredLanguage } from "@/lib/i18n";

// The client entry TanStack Start hydrates with (ADR-0053 §1). It replaces the
// old `main.tsx` bootstrap: the router + providers now live in `getRouter`
// (`src/router.tsx`), so all this owns is the boot-time side effects that must
// run before the app takes over, then handing off to `StartClient` — which
// builds the router via `getRouter` and hydrates the prerendered document.

// Mock Service Worker (dev / mock server only). Started BEFORE hydration so it
// can intercept the app's first requests; a no-op with ZERO cost otherwise.
async function enableMocking(): Promise<void> {
  if (import.meta.env.VITE_USE_MOCKS !== "true") return;
  const { worker } = await import("@/lib/mocks/browser");
  await worker.start({ onUnhandledRequest: "warn" });
}

async function main(): Promise<void> {
  await enableMocking();

  // Analytics (ADR-0050): consent-free cookieless, idempotent, and a no-op with
  // ZERO network when the key is blank or the visitor has opted out. Kicked off
  // at boot; it lazily loads posthog-js off the critical path.
  void initAnalytics();

  startTransition(() => {
    hydrateRoot(
      document,
      <StrictMode>
        <StartClient />
      </StrictMode>,
    );
  });

  // Re-apply the visitor's explicit prior language choice (footer switcher →
  // localStorage) AFTER the app has hydrated the prerendered PL HTML (ADR-0053 §3).
  // Deferred to a frame so the initial render matches the static HTML byte-for-byte
  // first — the language change is then an ordinary post-hydration update, never a
  // hydration mismatch. A no-op for a first-time visitor (stays PL) and on the server.
  requestAnimationFrame(applyStoredLanguage);
}

void main();
